import { useState } from 'react'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { useWallPosts } from './hooks/useWall'
import { useAuth } from '../../hooks/useAuth'
import { memberColors } from '../../styles/tokens'
import { getInitials, cn } from '../../lib/utils'
import { Spinner } from '../../components/ui/Spinner'
import { Button } from '../../components/ui/Button'

const REACTIONS = ['❤️', '😂', '👍', '🎉', '😮']

const Avatar = ({ profile }) => (
  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
    style={{ backgroundColor: memberColors[profile?.member_color ?? 'violet'] }}>
    {getInitials(profile?.display_name ?? '?')}
  </div>
)

const PostCard = ({ post, currentUserId, onDelete, onReact }) => {
  const [showReactPicker, setShowReactPicker] = useState(false)

  const reactionCounts = (post.wall_reactions ?? []).reduce((acc, r) => {
    acc[r.emoji] = (acc[r.emoji] ?? 0) + 1
    return acc
  }, {})

  const myReactions = new Set(
    (post.wall_reactions ?? []).filter((r) => r.user_id === currentUserId).map((r) => r.emoji)
  )

  return (
    <div className="bg-surface rounded-xl border border-border p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <Avatar profile={post.profiles} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary">{post.profiles?.display_name ?? 'Someone'}</p>
          <p className="text-xs text-text-muted">
            {formatDistanceToNow(parseISO(post.created_at), { addSuffix: true })}
          </p>
        </div>
        {post.profiles?.id === currentUserId && (
          <button onClick={() => onDelete(post.id)} className="text-text-muted hover:text-rose-500 p-1">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
            </svg>
          </button>
        )}
      </div>

      {/* Body */}
      <p className="text-sm text-text-primary whitespace-pre-wrap leading-relaxed">{post.body}</p>

      {/* Photo */}
      {post.photo_url && (
        <img src={post.photo_url} alt="" className="w-full rounded-lg object-cover max-h-64" />
      )}

      {/* Reactions bar */}
      <div className="flex items-center gap-2 flex-wrap">
        {Object.entries(reactionCounts).map(([emoji, count]) => (
          <button key={emoji}
            onClick={() => onReact(post.id, emoji)}
            className={cn('flex items-center gap-1 text-xs px-2 py-1 rounded-full border transition-colors',
              myReactions.has(emoji)
                ? 'bg-primary-light border-primary text-primary'
                : 'border-border text-text-muted hover:border-primary/40')}>
            <span>{emoji}</span>
            <span className="font-medium">{count}</span>
          </button>
        ))}
        <div className="relative">
          <button
            onClick={() => setShowReactPicker((v) => !v)}
            className="text-xs px-2 py-1 rounded-full border border-border text-text-muted hover:border-primary/40 transition-colors">
            + React
          </button>
          {showReactPicker && (
            <div className="absolute bottom-full left-0 mb-1 bg-surface border border-border rounded-xl shadow-card flex gap-1 p-1.5 z-10">
              {REACTIONS.map((e) => (
                <button key={e} onClick={() => { onReact(post.id, e); setShowReactPicker(false) }}
                  className="text-lg hover:scale-125 transition-transform w-8 h-8 flex items-center justify-center">
                  {e}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const ComposeBox = ({ onPost }) => {
  const [body,     setBody]    = useState('')
  const [photoUrl, setPhotoUrl] = useState('')
  const [saving,   setSaving]  = useState(false)
  const [error,    setError]   = useState('')

  const submit = async (e) => {
    e.preventDefault()
    if (!body.trim()) return
    setSaving(true)
    setError('')
    try {
      await onPost({ body: body.trim(), photo_url: photoUrl.trim() || null })
      setBody('')
      setPhotoUrl('')
    } catch (err) {
      setError(err?.message ?? 'Could not post. Please try again.')
    } finally { setSaving(false) }
  }

  return (
    <form onSubmit={submit} className="bg-surface rounded-xl border border-border p-4 space-y-3 mb-4">
      <textarea
        placeholder="What's going on in the family?"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        className="w-full text-sm text-text-primary placeholder:text-text-muted bg-transparent resize-none focus:outline-none"
      />
      <div className="flex items-center gap-2 border-t border-border pt-3">
        <input type="url" placeholder="Photo URL (optional)" value={photoUrl}
          onChange={(e) => setPhotoUrl(e.target.value)}
          className="flex-1 text-xs rounded-lg border border-border px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary" />
        <Button type="submit" size="sm" disabled={saving || !body.trim()}>
          {saving ? <Spinner size="sm" /> : 'Post'}
        </Button>
      </div>
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </form>
  )
}

export const WallPage = () => {
  const { user } = useAuth()
  const { posts, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, createPost, deletePost, toggleReaction } = useWallPosts()

  if (isLoading) return <div className="flex justify-center py-16"><Spinner /></div>

  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">
      <h1 className="text-xl font-bold text-text-primary mb-4">Family Wall</h1>

      <ComposeBox onPost={(fields) => createPost.mutateAsync(fields)} />

      {posts.length === 0 ? (
        <div className="text-center py-12 text-text-muted">
          <div className="text-5xl mb-3">📋</div>
          <p className="text-sm">Nothing posted yet — be the first!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              currentUserId={user?.id}
              onDelete={(id) => deletePost.mutate(id)}
              onReact={(postId, emoji) => toggleReaction.mutate({ postId, emoji })}
            />
          ))}

          {hasNextPage && (
            <div className="flex justify-center py-4">
              <Button variant="secondary" size="sm" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                {isFetchingNextPage ? <Spinner size="sm" /> : 'Load more'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
